/**
 * Google Maps Platform Configuration & Cost-Control Constants
 *
 * Security & Key Separation:
 * - Browser client uses VITE_GOOGLE_MAPS_API_KEY (restricted by HTTP Referrer + Maps JavaScript API & Places API New).
 * - Server-side proxy uses GOOGLE_MAPS_SERVER_API_KEY (or fallback VITE_GOOGLE_MAPS_API_KEY) for selective Routes API v2 calls.
 */

export const ALLOWED_NEARBY_RADII_KM = [5, 10, 20, 50] as const;
export type NearbyRadiusKm = (typeof ALLOWED_NEARBY_RADII_KM)[number];

export const DEFAULT_NEARBY_RADIUS_KM: NearbyRadiusKm = 20;

export const MAX_COMPARISON_PROFESSIONALS = 4;
export const MIN_COMPARISON_PROFESSIONALS = 2;

/**
 * Mandatory Google Maps Platform Attribution IDs for <Map> component
 */
export const GMP_ATTRIBUTION_IDS: string[] = [
  'gmp_mcp_codeassist_v1_aistudio',
  'gmp_git_agentskills_v1',
];

function cleanClientKey(raw?: unknown): string {
  if (typeof raw !== 'string') return '';
  let trimmed = raw.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    trimmed = trimmed.slice(1, -1).trim();
  }
  if (
    !trimmed ||
    trimmed === 'undefined' ||
    trimmed === 'null' ||
    trimmed === 'YOUR_API_KEY' ||
    trimmed === 'MY_GOOGLE_MAPS_API_KEY'
  ) {
    return '';
  }
  return trimmed;
}

/**
 * Resolves the browser Google Maps API key from Vite build-time env or runtime window.__JOBMATCH_ENV__
 */
export function getGoogleMapsBrowserApiKey(): string {
  const viteKey = cleanClientKey((import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY);
  if (viteKey) return viteKey;

  if (typeof window !== 'undefined' && (window as any).__JOBMATCH_ENV__) {
    const runtimeKey = cleanClientKey((window as any).__JOBMATCH_ENV__.VITE_GOOGLE_MAPS_API_KEY);
    if (runtimeKey) return runtimeKey;
  }

  return '';
}

/**
 * Resolves the Map ID required by AdvancedMarkerElement (defaults to DEMO_MAP_ID per GMP React spec)
 */
export function getGoogleMapsMapId(): string {
  const viteMapId = cleanClientKey((import.meta as any).env?.VITE_GOOGLE_MAPS_MAP_ID);
  if (viteMapId) return viteMapId;

  if (typeof window !== 'undefined' && (window as any).__JOBMATCH_ENV__) {
    const runtimeMapId = cleanClientKey((window as any).__JOBMATCH_ENV__.VITE_GOOGLE_MAPS_MAP_ID);
    if (runtimeMapId) return runtimeMapId;
  }

  return 'DEMO_MAP_ID';
}

export function isGoogleMapsConfigured(): boolean {
  return Boolean(getGoogleMapsBrowserApiKey());
}
